/**
 * The class strings the Blueprint components draw a tone with, written out in
 * full.
 *
 * ## WHY LOOKUP TABLES OF WHOLE CLASS NAMES
 *
 * Tailwind finds the classes it generates by reading source files for literal
 * strings. `bg-${tone}` is not a literal: it compiles, renders the right class
 * name into the DOM, and produces no CSS at all, so the mark is simply
 * invisible — and nothing fails, because the markup is "correct". Every tone a
 * component can be asked for therefore appears below as a complete class
 * name, once, and the components index into these records instead of building
 * strings. The rejected alternative was a Tailwind `@source inline(...)`
 * safelist in theme.css, which works until somebody adds a tone in TypeScript
 * and forgets the CSS file; a `Record<Tone, string>` is total, so a new tone
 * here is a compile error until it has its classes.
 *
 * ## WHAT THE TONES ARE ALLOWED TO MEAN
 *
 * `MarkTone` mixes two families on purpose, because a meter or a log entry can
 * legitimately be either: the status tones (`good`, `warn`, `bad`) are real
 * verdicts — data coverage is low, a delivery failed — and the identity tones
 * plus `brand` are decoration that only ever sits beside a word. Which one a
 * given call site may use is decided where the meaning is known, in the
 * caller; these tables only make sure each one is drawable.
 *
 * Nothing here is a text colour on a fill of a different family. The text
 * pairings that do appear (an identity colour on its own /16 or /24 wash) are
 * the ones theme.css measured; `verify-ui-suite` re-measures them.
 */
import type { IdentTone } from './identity';

/** Brand plus the identity slots: the decorative marks. */
export type DecorTone = 'brand' | IdentTone;

/** Anything a meter, a dot or a log icon may be drawn in. */
export type MarkTone = DecorTone | 'good' | 'warn' | 'bad';

/** A meter's fill. Brand is the horizontal sweep, never the primary-action gradient. */
export const FILL_CLASS: Readonly<Record<MarkTone, string>> = {
  brand: 'bg-brand-sweep',
  good: 'bg-good',
  warn: 'bg-warn',
  bad: 'bg-bad',
  'ident-a': 'bg-ident-a',
  'ident-b': 'bg-ident-b',
  'ident-c': 'bg-ident-c',
  'ident-d': 'bg-ident-d',
};

/**
 * An 8px identity dot beside a label. A solid colour rather than the sweep:
 * at 8px a gradient is mud. Brand is `brand-b`, which as a non-text mark
 * needs 3:1 and has 4.76:1 on surface-1.
 */
export const DOT_CLASS: Readonly<Record<MarkTone | 'neutral', string>> = {
  neutral: 'bg-muted',
  brand: 'bg-brand-b',
  good: 'bg-good',
  warn: 'bg-warn',
  bad: 'bg-bad',
  'ident-a': 'bg-ident-a',
  'ident-b': 'bg-ident-b',
  'ident-c': 'bg-ident-c',
  'ident-d': 'bg-ident-d',
};

/**
 * An icon or a glyph in a tone. Brand is `brand-ink`, not `brand-b`, because
 * an icon on `surface-2` in `brand-b` measures 4.47:1 and these icons carry
 * meaning alongside the text beside them.
 */
export const ICON_CLASS: Readonly<Record<MarkTone | 'neutral', string>> = {
  neutral: 'text-muted',
  brand: 'text-brand-ink',
  good: 'text-good',
  warn: 'text-warn',
  bad: 'text-bad',
  'ident-a': 'text-ident-a',
  'ident-b': 'text-ident-b',
  'ident-c': 'text-ident-c',
  'ident-d': 'text-ident-d',
};
